import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/formatters';
import MentionInput from './MentionInput';

export default function ThreadPanel({ entityType, entityId }) {
  const [comments, setComments] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState('');
  const { user } = useAuth();

  const fetchComments = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/${entityType}s/${entityId}/thread`);
      setComments(data.comments || []);
    } catch {}
  }, [entityType, entityId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  async function handleSubmit(body, mentions) {
    await api.post(`/api/${entityType}s/${entityId}/comments`, { body, mentions });
    fetchComments();
  }

  async function handleEdit(commentId) {
    await api.put(`/api/comments/${commentId}`, { body: editText });
    setEditing(null);
    fetchComments();
  }

  async function handleDelete(commentId) {
    if (!window.confirm('Delete this comment?')) return;
    await api.delete(`/api/comments/${commentId}`);
    fetchComments();
  }

  function renderBody(body) {
    return DOMPurify.sanitize(body.replace(/@(\w+(?:\s\w+)?)/g, '<span class="mention">@$1</span>'));
  }

  return (
    <div className="thread-panel">
      <h4 style={{ marginBottom: '12px' }}>Comments</h4>
      {comments.length === 0 && <p style={{ color: 'var(--text-light)', fontSize: '13px' }}>No comments yet</p>}
      {comments.map(c => (
        <div key={c.id} className="comment">
          <div className="comment-avatar">
            {c.author_avatar ? <img src={c.author_avatar} alt="" style={{ width: '100%', borderRadius: '50%' }} /> : (c.author_name || 'U').charAt(0)}
          </div>
          <div className="comment-body">
            <div className="comment-meta">
              <strong>{c.author_name}</strong> &middot; {formatDateTime(c.created_at)}
              {c.updated_at !== c.created_at && <span style={{ color: 'var(--text-light)' }}> (edited)</span>}
            </div>
            {editing === c.id ? (
              <div>
                <textarea value={editText} onChange={e => setEditText(e.target.value)} style={{ minHeight: '40px' }} />
                <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => handleEdit(c.id)}>Save</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="comment-text" dangerouslySetInnerHTML={{ __html: renderBody(c.body) }} />
            )}
            <div className="comment-actions">
              {user && c.user_id === user.id && editing !== c.id && (
                <button onClick={() => { setEditing(c.id); setEditText(c.body); }}>Edit</button>
              )}
              {user && (c.user_id === user.id || user.role === 'admin') && (
                <button onClick={() => handleDelete(c.id)}>Delete</button>
              )}
            </div>
          </div>
        </div>
      ))}
      {user && user.role !== 'view_only' && <MentionInput onSubmit={handleSubmit} />}
    </div>
  );
}
